/**
 * ConvertBlockAssets
 *
 * Handles dynamic loading of block CSS/JS assets after Convert A/B testing
 * injects new blocks into the page.
 *
 * Uses localized data from PHP (impBlockAssets) to know which blocks have assets.
 */
class ConvertBlockAssets {
	private loadedAssets: Set<string> = new Set();

	/**
	 * Initialize by cataloging already-loaded assets.
	 */
	init() {
		this.catalogLoadedAssets();
	}

	/**
	 * Catalog all CSS and JS assets already loaded on the page.
	 */
	private catalogLoadedAssets() {
		// Catalog loaded stylesheets
		document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]').forEach((link) => {
			this.loadedAssets.add(link.href);
		});

		// Catalog loaded scripts
		document.querySelectorAll<HTMLScriptElement>('script[src]').forEach((script) => {
			this.loadedAssets.add(script.src);
		});
	}

	/**
	 * Scan the DOM for blocks and load any missing assets.
	 * Call this after Convert has made changes.
	 */
	async loadMissingAssets() {
		if (typeof impBlockAssets === 'undefined') {
			return;
		}

		const blockNames = this.getBlockNamesFromDom();

		for (const blockName of blockNames) {
			await this.loadBlockAssets(blockName);
		}
	}

	/**
	 * Extract unique block names from the DOM by looking for .block-* classes.
	 */
	private getBlockNamesFromDom(): Set<string> {
		const blockNames = new Set<string>();

		// Find all elements with classes starting with "block-"
		document.querySelectorAll('[class*="block-"]').forEach((element) => {
			const classes = element.className.split(/\s+/);

			classes.forEach((className) => {
				// Match block names like "block-homepage-hero"
				// Stop at BEM modifiers (-- or __) to get the base block name
				const match = className.match(/^block-([a-z0-9]+(?:-[a-z0-9]+)*)(?:--|__|$)/);
				if (match && match[1]) {
					blockNames.add(match[1]);
				}
			});
		});

		return blockNames;
	}

	/**
	 * Load CSS and JS assets for a specific block if not already loaded.
	 * Waits for dependencies to load before loading block assets.
	 *
	 * @param  blockName
	 */
	private async loadBlockAssets(blockName: string) {
		const blockData = impBlockAssets.blocks[blockName];

		// Block doesn't exist in our registry
		if (!blockData) {
			return;
		}

		const { themeUrl, dependencyUrls } = impBlockAssets;
		const baseCssPath = `${themeUrl}/acf-blocks/${blockName}/dist/css/style.css`;
		const baseJsPath = `${themeUrl}/acf-blocks/${blockName}/dist/js/script.js`;
		const cssPath = blockData.cssVer ? `${baseCssPath}?ver=${blockData.cssVer}` : baseCssPath;
		const jsPath = blockData.jsVer ? `${baseJsPath}?ver=${blockData.jsVer}` : baseJsPath;

		// Load CSS dependencies (can load in parallel)
		if (blockData.cssDeps) {
			await Promise.all(
				blockData.cssDeps.map((dep) => this.loadStyleDependency(dep, dependencyUrls))
			);
		}

		// Load CSS if block has it and not already loaded
		if (blockData.css && !this.isAssetLoaded(cssPath)) {
			await this.loadStylesheet(cssPath, blockName);
		}

		// Load JS dependencies sequentially (order may matter)
		if (blockData.jsDeps) {
			for (const dep of blockData.jsDeps) {
				await this.loadScriptDependency(dep, dependencyUrls);
			}
		}

		// Load JS if block has it and not already loaded (after deps are ready)
		if (blockData.js && !this.isAssetLoaded(jsPath)) {
			await this.loadScript(jsPath, blockName);
		}
	}

	/**
	 * Load a style dependency by its handle, including its own dependencies.
	 *
	 * @param  handle
	 * @param  dependencyUrls
	 */
	private async loadStyleDependency(
		handle: string,
		dependencyUrls: typeof impBlockAssets.dependencyUrls
	): Promise<void> {
		const depInfo = dependencyUrls[handle];
		const url = depInfo?.styleUrl;

		if (!url || this.isAssetLoaded(url)) {
			return;
		}

		// Load this dependency's own style dependencies first
		if (depInfo.styleDeps && depInfo.styleDeps.length > 0) {
			for (const subDep of depInfo.styleDeps) {
				await this.loadStyleDependency(subDep, dependencyUrls);
			}
		}

		await this.loadStylesheet(url, `dep-${handle}`);
	}

	/**
	 * Load a script dependency by its handle, including its own dependencies.
	 *
	 * @param  handle
	 * @param  dependencyUrls
	 */
	private async loadScriptDependency(
		handle: string,
		dependencyUrls: typeof impBlockAssets.dependencyUrls
	): Promise<void> {
		const depInfo = dependencyUrls[handle];
		const url = depInfo?.scriptUrl;

		if (!url || this.isAssetLoaded(url)) {
			return;
		}

		// Load this dependency's own script dependencies first
		if (depInfo.scriptDeps && depInfo.scriptDeps.length > 0) {
			for (const subDep of depInfo.scriptDeps) {
				await this.loadScriptDependency(subDep, dependencyUrls);
			}
		}

		await this.loadScript(url, `dep-${handle}`);
	}

	/**
	 * Check if an asset URL is already loaded.
	 *
	 * @param  url
	 */
	private isAssetLoaded(url: string): boolean {
		if (!url) {
			return true; // Treat empty URLs as "loaded" to skip them
		}

		// Check our catalog
		if (this.loadedAssets.has(url)) {
			return true;
		}

		// Also check for partial URL matches (handles query strings, etc.)
		for (const loadedUrl of this.loadedAssets) {
			if (loadedUrl.includes(url) || url.includes(loadedUrl.split('?')[0])) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Dynamically load a stylesheet and wait for it to load.
	 *
	 * @param  url
	 * @param  blockName
	 */
	private loadStylesheet(url: string, blockName: string): Promise<void> {
		return new Promise((resolve) => {
			if (!url) {
				resolve();
				return;
			}

			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = url;
			link.dataset.convertBlock = blockName;

			link.onload = () => {
				this.loadedAssets.add(url);
				resolve();
			};

			link.onerror = () => {
				// Resolve anyway to not block other assets
				resolve();
			};

			document.head.appendChild(link);
		});
	}

	/**
	 * Dynamically load a script and wait for it to load.
	 *
	 * @param  url
	 * @param  blockName
	 */
	private loadScript(url: string, blockName: string): Promise<void> {
		return new Promise((resolve) => {
			if (!url) {
				resolve();
				return;
			}

			const script = document.createElement('script');
			script.src = url;
			script.dataset.convertBlock = blockName;

			script.onload = () => {
				this.loadedAssets.add(url);
				resolve();
			};

			script.onerror = () => {
				// Resolve anyway to not block other assets
				resolve();
			};

			document.body.appendChild(script);
		});
	}
}

export default new ConvertBlockAssets();
